import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface LockIndicatorProps {
  locked: boolean;
  queuePosition?: number;
  activeUser?: {
    id: string;
    display_name: string;
  } | null;
  isOwnedByCurrentUser?: boolean;
  /** ms-since-epoch when the agent started working — drives the elapsed clock. */
  lockedSince?: number | null;
  /** Number of tool calls used during the active turn. */
  toolsUsed?: number;
}

/** mm:ss format for short-running agent turns; falls back to hh:mm:ss past one hour. */
function formatElapsedMs(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  return hours > 0
    ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

export function LockIndicator({
  locked,
  queuePosition,
  activeUser,
  isOwnedByCurrentUser = false,
  lockedSince,
  toolsUsed = 0,
}: LockIndicatorProps): React.ReactElement {
  const showOwnershipLock = activeUser != null && !isOwnedByCurrentUser;
  const visible = locked || showOwnershipLock;

  // Tick once per second while locked so the displayed elapsed updates.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!locked || lockedSince == null) return;
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    return (): void => {
      clearInterval(interval);
    };
  }, [locked, lockedSince]);
  const elapsedLabel =
    locked && lockedSince != null ? formatElapsedMs(Date.now() - lockedSince) : null;

  return (
    <div
      className={cn(
        'overflow-hidden transition-all duration-300',
        visible ? 'h-8 opacity-100' : 'h-0 opacity-0'
      )}
    >
      <div
        className={cn(
          'flex h-8 items-center gap-2 border-t px-6',
          showOwnershipLock
            ? 'border-warning/30 bg-[oklch(0.78_0.16_75/0.05)]'
            : 'border-accent-bright/30 bg-[oklch(0.72_0.14_225/0.05)]'
        )}
      >
        {showOwnershipLock ? (
          <span className="size-2 rounded-full bg-warning" />
        ) : (
          <span className="fd-sweep size-2.5" />
        )}
        <span
          className={cn(
            'text-xs font-medium',
            showOwnershipLock ? 'text-warning' : 'text-accent-bright'
          )}
        >
          {showOwnershipLock
            ? `${activeUser.display_name} is currently editing this conversation`
            : 'Agent is working…'}
        </span>
        {!showOwnershipLock && elapsedLabel && (
          <span className="font-mono text-[11px] text-text-tertiary">
            elapsed {elapsedLabel}
            {toolsUsed > 0 && ` · ${String(toolsUsed)} tool${toolsUsed === 1 ? '' : 's'} used`}
          </span>
        )}
        {!showOwnershipLock && queuePosition !== undefined && queuePosition > 0 && (
          <span className="font-mono text-[11px] text-text-tertiary">
            Position {String(queuePosition)} in queue
          </span>
        )}
      </div>
    </div>
  );
}
